import React, { Fragment, useState, useEffect } from "react";
import { useEffectOnce } from "../../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import {
  setProcessing,
  loadPage,
  setPageValue,
  setPanes,
  deleteEntity,
  entityDeleted,
  disposePage,
  setOpenCreateConnectionPopup,
  getConnectors,
  redirectToGoogleAuth,
  setOpenUpdateConnectionPopup,
  setUpdatingConnection,
  updateConnection,
} from "../../../redux/slices/connectionlocatorpageslice";
import { addNotification } from "../../../redux/slices/notificationsslice";
import {
  hideAppDialog,
  setAppDialog,
} from "../../../redux/slices/appdialogslice";
import { apiErrorsHandler, getErrorMessages } from "../../../api/httpclient";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { Splitter } from "@progress/kendo-react-layout";
import { Page } from "../../page";
import { Loader } from "@progress/kendo-react-indicators";
import { Input } from "@progress/kendo-react-inputs";
import { notificationDurationMiddle } from "../../../lib/utils";
import { ReactComponent as ImportIcon } from "../../../icons/import.svg";

import "./connectionlocatorpage.css";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { ConnectionViewer } from "../../../components/connectionviewer";
import { Dialog } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";

export const ConnectionLocatorPage = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.connectionLocatorPage);
  const appearance = useSelector((state) => state.appearance);
  const filter = pageState.filter;
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const params = new URLSearchParams(location.search);
  const success = params.get("success");

  const [handleAnimation, setHandleAnimation] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (handleAnimation) setHandleAnimation(false);
    }, 1500);
  });

  useEffect(() => { 
    if (!pageState.items || pageState.items?.length === 0) {
      Cookies.set("noconnection", "true", { expires: 1 });
    } else {
      Cookies.remove("noconnection");
    }
  }, [pageState.items]);

  useEffect(() => {
    if (pageState.openCreateConnectionPopup) {
      dispatch(getConnectors(t));
    }
  }, [pageState.openCreateConnectionPopup]);

  /*load with success param*/
  useEffect(() => { 
    if (!success) return;

    if (success == "true" || success == true) {
      dispatch(
        addNotification({
          text: t("connection.createdSuccessfully"),
          type: "success",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        })
      );
    }

    if (success == "false" || success == false) {
      dispatch(
        addNotification({
          text: t("connection.creationFailed"),
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        })
      );
    }
  }, [success]);

  /*initial page load*/
  useEffectOnce(() => { 
    dispatch(loadPage(currentUser, t)).then(
      (response) => {},
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.locator.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.locator.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );

    return () => {
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    dispatch(setPanes(e.newState));
  };
  /*splitter*/

  const handleOnDelete = (id) => {
    dispatch(
      setAppDialog({
        isVisible: true,
        className: "connectionlocator-page-are-you-sure",
        title: t("connection.warning"),
        content: t("schedule.areyousure"),
        actions: {
          yesButton: {
            title: t("datasets.locator.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(deleteEntity(id, t)).then(
                (response) => {
                  dispatch(
                    addNotification({
                      text: t("connection.deletingSuccess"),
                      type: "success",
                      closable: true,
                      duration: notificationDurationMiddle,
                      position: "topRight",
                    })
                  );
                },
                (err) => {
                  dispatch(setProcessing({ processing: false }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [t("datasets.locator.unknown_error")];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);

                      if (payload.data.ErrorCodes.EntityDoesNotExist)
                        dispatch(entityDeleted(id));
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessage = t("datasets.locator.unknown_error");
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                }
              );
            },
          },
          noButton: { title: t("datasets.locator.no") },
        },
      })
    );
  };

  const handleOnUpdate = (id) => {
    dispatch(
      updateConnection(
        id,
        pageState?.updatingConnection?.ConnectionTitle,
        pageState?.updatingConnection?.IsPublic,
        t
      )
    ).then(
      (response) => {
        dispatch(
          addNotification({
            text: t("connection.updatedConnection"),
            type: "success",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
      },
      (err) => {
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datasets.locator.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
          }

          dispatch(
            addNotification({
              text: `${errorMessages[0]} ${t("connection.updateFailed")}`,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datasets.locator.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  return (
    <Fragment>
      <Page
        id="connectionLocatorPage"
        className="connectionlocator-page"
        title={t("connection.connectionview")}
        animation={props.animation}
      >
        <Fragment>
          {pageState.processing && (
            <div className="page-loader">
              <Loader
                size={"large"}
                type="infinite-spinner"
                themeColor={"primary"}
              />
              {pageState.processingText && (
                <div style={{ fontSize: 13, color: currentTheme.primary }}>
                  {pageState.processingText}
                </div>
              )}
            </div>
          )}
          {pageState.pageLoaded && (
            <Fragment>
              <div className="connectionlocator-desktop-view">
                <Splitter
                  style={{ border: "none" }}
                  panes={pageState.panes}
                  onChange={handleOnSplitterChange}
                >
                  <div className="connectionlocator-page-manager">
                    {pageState.canCreate && (
                      <div className="new-dataset">
                        <div
                          className="new-container"
                          style={{}}
                          onClick={() =>
                            dispatch(setOpenCreateConnectionPopup(true))
                          }
                        >
                          <div>
                            <div className="k-icon k-i-plus"></div>
                            <div>{t("connection.newConnection")}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="filter-dataset">
                      <div
                        style={{
                          marginTop: 4,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Input
                          onChange={(e) => {
                            setHandleAnimation(true);
                            dispatch(
                              setPageValue({ id: "filter", value: e.value })
                            );
                          }}
                          value={filter}
                          type="search"
                          autoFocus={true}
                          style={{ flex: 1, padding: 8 }}
                          placeholder={t("datasets.locator.search")}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={`right ${handleAnimation ? "fade" : null}`}>
                    <div className="dataset-locator-wrapper">
                      <div className="title">{t("connection.connectionview")}</div>
                      <ConnectionViewer
                        canView={pageState.canView}
                        canCreate={pageState.canCreate}
                        canEdit={pageState.canEdit}
                        canDelete={pageState.canDelete}
                        onDelete={handleOnDelete}
                        onEdit={(connection) => {
                          dispatch(setUpdatingConnection(connection));
                          dispatch(setOpenUpdateConnectionPopup(true));
                        }}
                      />
                    </div>
                  </div>
                </Splitter>
              </div>
              <div className="connectionlocator-mobile-view">
                <div className="connectionlocator-mobile-heading">
                  <h1>{t("connection.connectionview")}</h1>
                </div>
                {pageState.canCreate && (
                  <Button
                    className="connectionlocator-mobile-create"
                    icon="plus"
                    fillMode="outline"
                    onClick={() => dispatch(setOpenCreateConnectionPopup(true))}
                  >
                    {t("connection.newConnection")}
                  </Button>
                )}
                <Input
                  onChange={(e) => dispatch(setPageValue({ id: "filter", value: e.value }))}
                  value={filter}
                  type="search"
                  placeholder={t("datasets.locator.search")}
                  className="connectionlocator-mobile-search"
                />
                <div className="connectionlocator-mobile-list">
                  <ConnectionViewer
                    canView={pageState.canView}
                    canCreate={pageState.canCreate}
                    canEdit={pageState.canEdit}
                    canDelete={pageState.canDelete}
                    onDelete={handleOnDelete}
                    onEdit={(connection) => {
                      dispatch(setUpdatingConnection(connection)); // store selected connection
                      dispatch(setOpenUpdateConnectionPopup(true)); // open popup
                    }}
                  />
                </div>
              </div>
            </Fragment>
          )}
        </Fragment>
      </Page>
      {pageState?.openCreateConnectionPopup && (
        <Dialog
          title={t("connection.create")}
          className="connection-form-dialog"
          onClose={() => {
            dispatch(setOpenCreateConnectionPopup(false));
            dispatch(
              setPageValue({
                id: "selectedConnector",
                value: null,
              })
            );
          }}
        >
          <div
            style={{ display: "flex", flexDirection: "column", width: "100%" }}
          >
            <Label style={{ marginBottom: "5px" }}>
              {" "}
              {t("connection.selectConnector")}
            </Label>
            <DropDownList
              data={pageState.connectors}
              textField="Title"
              dataItemKey="Id"
              value={pageState.selectedConnector}
              defaultItem={{
                Id: null,
                Title: t("connection.selectConnector"),
              }}
              onChange={(e) => {
                if (!e.value) return;
                dispatch(
                  setPageValue({
                    id: "selectedConnector",
                    value: e.value,
                  })
                );
                dispatch(setOpenCreateConnectionPopup(false));
                dispatch(redirectToGoogleAuth(e.value.Id, t));
              }}
              currentTheme={currentTheme}
            />
          </div>
        </Dialog>
      )}

      {pageState?.openUpdateConnectionPopup && (
        <Dialog
          title={t("connection.update")}
          className="connection-form-dialog"
          onClose={() => {
            dispatch(setOpenUpdateConnectionPopup(false));
            dispatch(
              setPageValue({
                id: "updatingConnection",
                value: {
                  Id: pageState?.updatingConnection.Id,
                  ConnectionTitle: pageState?.updatingConnection?.ConnectionTitle,
                  IsPublic: pageState?.updatingConnection?.IsPublic,
                },
              })
            );
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            {/* Connection Title */}

            <Label> {t("connection.title")}</Label>
            <Input
              value={pageState?.updatingConnection?.ConnectionTitle}
              onChange={(e) =>
                dispatch(
                  setPageValue({
                    id: "updatingConnection",
                    value: {
                      ...pageState?.updatingConnection,
                      ConnectionTitle: e.value,
                    },
                  })
                )
              }
            />

            {/* IsPublic */}
            {currentUser?.IsFederationRootUser && (
              <Checkbox
                label={t("connection.isPublic")}
                checked={pageState?.updatingConnection?.IsPublic}
                style={{ marginTop: "15px" }}
                onChange={(e) =>
                  dispatch(
                    setPageValue({
                      id: "updatingConnection",
                      value: {
                        ...pageState?.updatingConnection,
                        IsPublic: e.value,
                      },
                    })
                  )
                }
              />
            )}

            {/* Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "20px",
              }}
            >
              <Button
                style={{ borderRadius: "0" }}
                onClick={() => dispatch(setOpenUpdateConnectionPopup(false))}
              >
                {t("connection.cancel")}
              </Button>
              <Button
                themeColor="primary"
                style={{ borderRadius: "0" }}
                onClick={(e) =>
                  handleOnUpdate(pageState?.updatingConnection?.ConnectionId)
                }
              >
                {t("connection.save")}
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      <style>
        {`
                /*popups*/
                .connection-form-dialog .k-dialog{
                  border-radius:0;  
                  direction:${
                    appearance.isRightToLeft ? "rtl" : "ltr"
                  }                 
                }
                .connection-form-dialog .k-dialog-content
                { 
                  width: 400px;
                  max-width: 100%;
                  border-radius:0;
                  min-height:100px;
                }
                  .connection-form-dialog .k-dialog-content .k-input{
                      padding: 8px;
                      margin-top: 4px;
                      width: 100%;
                      border-radius: 0;
                  }

                 .connection-form-dialog .k-dialog-content .k-checkbox-label{
                 margin-inline-start:10px;
                 top:7px;
                 }

                @media (max-width: 768px) {
                  .connection-form-dialog .k-dialog {
                    width: calc(100vw - 32px);
                    max-width: calc(100vw - 32px);
                    min-width: 0;
                    max-height: calc(100dvh - 32px);
                  }

                  .connection-form-dialog .k-dialog-titlebar {
                    min-width: 0;
                  }

                  .connection-form-dialog .k-dialog-title {
                    overflow-wrap: anywhere;
                  }

                  .connection-form-dialog .k-dialog-content {
                    width: 100%;
                    min-width: 0;
                    max-width: 100%;
                    max-height: calc(100dvh - 112px);
                    overflow-y: auto;
                  }
                }

                .connectionlocator-page-are-you-sure .k-dialog
                { 
                  width: 400px;
                  border-radius:0;
                  min-height:100px;
                }
                    /*page class*/
                    .connectionlocator-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding}; 
                    }

                    .connectionlocator-wrapper{ 
                        direction:${
                          appearance.isRightToLeft ? "rtl" : "ltr"
                        }                      
                    }    
                    .connectionlocator-page .k-splitter{
                      flex-direction:${
                        appearance.isRightToLeft ? "row-reverse" : "row"
                      } 
                    }
                      
                    .connectionlocator-page .k-splitter .k-pane{
                      direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                    }

                    .connectionlocator-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .connectionlocator-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                   
                    /*are you sure to delete*/
                    .connectionlocator-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .connectionlocator-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }


                    /*the manager*/
                    .connectionlocator-page > .content .schedulelocator-page-manager
                    {
                        display: flex;
                        flex-direction: column;
                        flex-wrap: nowrap;
                        height: 100%;
                        overflow: hidden;/* auto;*/
                        padding: 0 0 8px 0;
                        border: ${currentTheme.managerBorder};
                        border-radius: ${currentTheme.pageBorderRadius};
                        background-color: ${currentTheme.componentBgFaded};
                    }

                    .connectionlocator-page > .content .connectionlocator-page-manager .k-dropdownlist:focus,
                    .connectionlocator-page > .content .connectionlocator-page-manager .k-input:focus
                    {
                        box-shadow: none;
                    }

                    .connectionlocator-page > .content .connectionlocator-page-manager .k-dropdownlist,
                    .connectionlocator-page > .content .connectionlocator-page-manager .k-input
                    {
                        background-color: transparent;
                        color: ${currentTheme.componentText};
                    }


                    /*new dataset*/
                    .connectionlocator-page .new-dataset 
                    {
                        display: flex;
                        flex-direction:column;
                        justify-content: center;
                        align-items: center;
                        padding: 32px 16px;

                    }

                    .connectionlocator-page .new-dataset a
                    {
                        width: 100%;
                        display: inline-block;
                        color: ${currentTheme.componentText};
                        text-decoration: none;
                        margin-bottom:15px;
                    }

                    .connectionlocator-page .new-dataset .new-container
                    {
                        padding: 16px 16px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        border: 1px solid ${currentTheme.separatorColor};
                        border-radius: 8px;
                        color: ${currentTheme.componentText};   
                        opacity: 0.5;
                    }
                    
                    .connectionlocator-page .new-dataset .new-container:hover
                    {
                        cursor: pointer;
                        background-color: ${currentTheme.componentBgFaded};
                        border-color: ${currentTheme.separatorColor};
                        opacity: 1;
                        transition: opacity 1000ms;
                    }

                    .connectionlocator-page .new-dataset .new-container > div
                    {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }                    

                    .connectionlocator-page .new-dataset .new-container .k-icon
                    {
                        color: ${currentTheme.componentText};   
                        font-size: 20px;
                        font-weight: 300;
                        margin-right: 4px;
                    }    
                    
                    .connectionlocator-page .new-dataset .new-container svg
                    {
                        color: ${currentTheme.componentText};   
                        margin-right: ${appearance.isRightToLeft ? "0" : "4px"};
                        margin-left: ${appearance.isRightToLeft ? "4px" : "0"};
                    }                                   

                    .connectionlocator-page .filter-dataset
                    {
                        display: flex;
                        flex-direction: column;
                        margin-top: 0px;
                        padding: 32px 16px;
                        border-top: 1px solid ${
                          currentTheme.componentBorderColor
                        };
                    }

                    .connectionlocator-page .filter-dataset .k-input::placeholder
                    {
                        color: ${currentTheme.componentText};
                        font-style: italic;
                    }

                    .connectionlocator-page .filter-dataset .k-button
                    {
                        background-color: transparent;
                        border-color: ${currentTheme.separatorColor};
                        border-radius: 50%;
                    }

                    .connectionlocator-page .filter-dataset .k-button .k-icon
                    {
                        color: ${currentTheme.componentText};
                    }

                    .connectionlocator-page .filter-dataset .k-button:hover
                    {
                        background-color: ${currentTheme.separatorColor};
                    }

                    .connectionlocator-page .fav-dataset
                    {
                        display: flex;
                        flex-direction: column;
                        padding: 32px 16px 16px 16px;
                        height: 100%;
                        overflow-y: hidden;
                        border-top: 1px solid ${
                          currentTheme.componentBorderColor
                        };
                    }

                    .connectionlocator-page .fav-dataset .title
                    {
                        color: ${currentTheme.componentText};
                        font-weight: 400;
                        font-size: 16px;
                        margin-bottom: 6px;
                        margin-top: 0px;
                        padding-bottom: 6px;
                        border-bottom: 1px solid ${
                          currentTheme.componentBorderColor
                        };
                    }

                    /*fav list*/
                    .connectionlocator-page .fav-dataset .list-container
                    {
                        /*border: 1px dashed ${currentTheme.separatorColor};*/
                        border-radius: 6px;
                        padding: 0;
                        overflow-y: auto;
                    }

                    .connectionlocator-page .fav-dataset .list-root
                    {
                        border-spacing: 0 6px;/*vertical space between 2 rows*/
                    }

                    .connectionlocator-page .fav-dataset .list-root .list-item
                    {
                    }

                    .connectionlocator-page .fav-dataset .fav-connection .list-item .list-item-img
                    {
                        
                    }
                    .connectionlocator-page .fav-dataset .fav-connection .list-item .list-item-img img
                    {
                        width: 40px;
                        height: auto;
                        aspect-ratio: 4 / 3;
                        border-radius: 6px;
                    }

                    .connectionlocator-page .fav-dataset .fav-connection .list-item .list-item-title
                    {
                        width: 100%;
                        vertical-align: middle;/*fine tuning*/
                    }
                    
                    .connectionlocator-page .fav-dataset .list-root .list-item-view-dataset a,
                    .connectionlocator-page .fav-dataset .list-root .list-item-edit-dataset a                    
                    {                
                        padding: 4px;
                        /*border: 1px solid ${
                          currentTheme.componentBorderColor
                        };
                        border-radius: 4px;*/
                        border: none;
                        display: flex;
                        align-items: center;
                        color: ${currentTheme.touchColor}!important;
                        text-decoration: none;
                    } 

                    .connectionlocator-page .fav-dataset .list-root .list-item-remove            
                    {                                       
                        color: ${currentTheme.error}!important;
                    } 

                    .connectionlocator-page .fav-dataset .fav-connection .list-item-remove:hover
                    {
                        color: ${currentTheme.touchColor};
                        cursor: pointer;
                    }

                    .connectionlocator-page .right                    
                    {
                        background-color: transparent;
                        height: 100%;
                        padding-left: 0px;
                        border: ${currentTheme.pageFormBorder};   
                        border-radius: ${currentTheme.pageBorderRadius};
                        display: flex;
                        flex-direction: column;
                    }                    

                    .connectionlocator-page .right.fade
                    {
                        animation-name: hide-dataset;
                        animation-duration: 2s;
                    }

                    @keyframes hide-dataset {
                        0%   {opacity:0};}
                    }

                    .connectionlocator-page .dataset-locator-wrapper{
                    }

                    .connectionlocator-page .dataset-locator-wrapper .title
                    {
                        font-size: 17px;
                        font-weight: 700;
                        color: ${currentTheme.touchColor};
                        padding: 6px 16px 6px 16px;
                        font-style: italic;
                        border-bottom: ${currentTheme.pageFormBorder};
                    }

                    .connectionlocator-desktop-view {
                      flex: 0 0 100%;
                      width: 100%;
                      min-width: 0;
                      height: 100%;
                    }

                    .connectionlocator-desktop-view .k-splitter {
                      width: 100%;
                      height: 100%;
                    }

                    .connectionlocator-mobile-view {
                      display: none;
                    }

                    @media (max-width: 1024px) {
                      .connectionlocator-page > .content {
                        overflow-y: auto;
                      }

                      .connectionlocator-desktop-view {
                        display: none;
                      }

                      .connectionlocator-mobile-view {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                        min-height: 100%;
                        padding: 18px 16px;
                        box-sizing: border-box;
                        background: ${currentTheme.componentBg};
                        color: ${currentTheme.componentText};
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"};
                      }

                      .connectionlocator-mobile-heading h1 {
                        margin: 0 0 18px;
                        color: ${currentTheme.componentText};
                        font-size: 21px;
                        font-weight: 600;
                      }

                      .connectionlocator-page .connectionlocator-mobile-create {
                        display: none;
                      }

                      .connectionlocator-page .connectionlocator-mobile-search {
                        margin-bottom: 12px;
                        background: transparent;
                      }

                      .connectionlocator-mobile-list {
                        width: 100%;
                        min-width: 0;
                      }
                    }

                   

                    `}
      </style>
    </Fragment>
  );
};
